import { nullable } from '@shared/common/common-types';
import { isNil } from 'lodash';

// Characters that can never appear in an email address - whitespace, control and format characters
// (`\p{C}`, which is what catches the invisible zero-width space a copy from a web page carries),
// the RFC specials that only carry meaning in a quoted local part, and the bracket family. Pasted
// junk usually consists of exactly these, and dropping them turns a mangled address back into a
// matchable one.
const emailIllegalCharsRegex = /[\s\p{C},:;()<>[\]\\"/{}]/gu;
const consecutiveDotsRegex = /\.{2,}/g;
const edgeDotsRegex = /^\.+|\.+$/g;
const nonDigitsRegex = /\D/g;
// Deliberately without the `g` flag - these are used with `.test()`, which is stateful when global.
const digitRegex = /\d/;
const letterRegex = /\p{L}/u;
const emailMarker = '@';
const plusSign = '+';

/**
 * Splits a search term the same way the backend does (`ClientByEmailOrPhoneSpecification`) - a term
 * carrying an `@` is looked up against emails, everything else against phones.
 */
function isEmailSearchTerm(value?: nullable<string>): boolean {
    return !isNil(value) && value.includes(emailMarker);
}

/**
 * Reduces a phone-shaped term to digits, keeping a single leading `+` so `normalizePhoneNumber`
 * can still tell an international number from a national one.
 */
function sanitizePhoneSearchTerm(value: string): string {
    const digits = value.replace(nonDigitsRegex, '');

    return value.startsWith(plusSign)
        ? `${plusSign}${digits}`
        : digits;
}

/**
 * Strips characters that can belong to neither a phone nor an email from a user-typed contact
 * search term, so junk pasted along with the value (`+359878407862,.>?!"}{_+`) still resolves to
 * the existing contact instead of falling through to the create form. Safe to call on every
 * keystroke: it only ever removes characters, and terms that are still ambiguous are left alone.
 *
 * - a term containing `@` is cleaned as an email;
 * - a term with digits and no letters is cleaned as a phone;
 * - anything else (names, free text, half-typed input like `todor2`) is returned unchanged - the
 *   no-letter guard is what keeps `todor2005@gmail.com` typeable one character at a time.
 */
function sanitizeContactSearchInput(value?: nullable<string>): string {
    if (isNil(value)) {
        return '';
    }

    if (isEmailSearchTerm(value)) {
        return value.replace(emailIllegalCharsRegex, '');
    }

    return digitRegex.test(value) && !letterRegex.test(value)
        ? sanitizePhoneSearchTerm(value.trim())
        : value;
}

/**
 * Repairs dot placement in an email: two dots can never sit next to each other, and a dot can be
 * neither the first nor the last character of the local part or of the domain - hence the trimming
 * per `@`-delimited part rather than over the whole address.
 */
function repairEmailDots(value: string): string {
    return value
        .replace(consecutiveDotsRegex, '.')
        .split(emailMarker)
        .map((part) => part.replace(edgeDotsRegex, ''))
        .join(emailMarker);
}

/**
 * The submit-time counterpart of `sanitizeContactSearchInput`, mirroring the backend's
 * `StringExtensions.SanitizeContactSearchTerm`. On top of removing illegal characters it repairs
 * dot placement in emails, which must not happen while typing - trimming a trailing dot on every
 * keystroke would make an address with dots impossible to type.
 */
function sanitizeContactSearchTerm(value?: nullable<string>): string {
    const sanitized = sanitizeContactSearchInput(value)
        .trim();

    return isEmailSearchTerm(sanitized)
        ? repairEmailDots(sanitized)
        : sanitized;
}

export {
    isEmailSearchTerm,
    sanitizeContactSearchInput,
    sanitizeContactSearchTerm,
};
